import { ChatUserModel } from 'types/chat-users/chat-user';
import { ContactResponse } from 'types/contacts/contact-response';
import { MessageDirection, MessageMediaType, MessageStatus } from 'types/chat/messages/common';
import { store } from 'store';

/**
 * ממיפה את התשובה החדשה מהשרת למבנה ChatUserModel הקיים
 */
export function mapContactToChatUser(contact: ContactResponse): ChatUserModel {
  const lastMessage = contact.lastMessageObject;
  const lastIncoming = contact.lastIncomingMessage;
  const lastOutgoing = contact.lastOutgoingMessage;

  // קביעת שם מלא - עדיפות ל-name, אחרת firstName + lastName
  const fullName = contact.name || 
    [contact.firstName, contact.lastName].filter(Boolean).join(' ') || 
    contact.phone;

  // קביעת CompanyId מה-store (אם קיים)
  const companyId = store.getState().company?.CompanyId || '';

  // קביעת NotificationContactName ו-NotificationContactUser מ-manager (משויך)
  // manager הוא מה שהיה assignee - השדה עבר ל-lastSession.manager
  let notificationContactName: string | null = null;
  let notificationContactUser: string | null = null;
  
  const manager = contact.lastSession?.manager;
  
  if (manager?.id) {
    // השתמש ב-manager (משויך)
    notificationContactUser = manager.id;
    notificationContactName = manager.fullName;
  }

  // מיפוי ההודעה האחרונה מ-lastMessageObject
  const lastMessageDate = lastMessage?.dateCreated || null;
  const lastMessageText = lastMessage?.message || null;
  const lastMessageType = lastMessage?.direction === 'incoming' ? 'incoming' : 
                         lastMessage?.direction === 'outgoing' ? 'outcoming' : null;
  const lastMessageMediaType = lastMessage ? mapMessageTypeToMediaType(lastMessage.type) : null;
  const lastMessageSource = lastMessage?.status || null;

  return {
    Id: contact.id,
    UserDisplayPhoneNumber: contact.phone,
    DateCreate: contact.lastSession?.dateStart || new Date().toISOString(),
    CompanyID: companyId,
    FirstName: contact.firstName || '',
    LastName: contact.lastName || '',
    FullName: fullName,
    Email: contact.email || '',
    LastMessageDate: lastMessageDate,
    LastMessage: lastMessageText,
    OutcomingLastMessageDate: lastOutgoing?.dateCreated || null,
    OutcomingLastMessage: lastOutgoing?.message || null,
    OutcomingLastMessageId: lastOutgoing?.id || null,
    OutcomingLastMessageStatus: mapStatusToMessageStatus(lastOutgoing?.status) || null,
    IncomingLastMessageDate: lastIncoming?.dateCreated || null,
    IncomingLastMessage: lastIncoming?.message || null,
    LastIncomingMessage: lastIncoming || {},
    LastOutgoingMsgObject: lastOutgoing || {},
    LastMessageType: lastMessageType,
    LastMessageMediaType: lastMessageMediaType,
    LastMessageSource: lastMessageSource,
    NotificationContactUser: notificationContactUser,
    IsOpen: contact.lastSession?.status === 'open' 
      ? true 
      : contact.lastSession?.status === 'closed' 
        ? false 
        : contact.isOpen ?? false,
    isRestricted24: contact.isWhatsappWindowOpen, // מיפוי: isWhatsappWindowOpen מהשרת → isRestricted24 בקוד
    AccountFullName: '',
    ContactFullName: fullName,
    WhatsappFullName: fullName,
    AccountPowerlinkId: contact.fireberryId || null,
    ContactPowerlinkId: contact.fireberryId || null,
    ObjectPowerlinkId: contact.fireberryId || null,
    NotificationContactName: notificationContactName,
    UnreadMessagesNumber: contact.lastSession?.unreadCount ?? contact.unreadCount ?? 0,
    UserPic: contact.pic || null,
    LableName: contact.lastSession?.label || contact.label || null,
    incomingLastMessageId: lastIncoming?.id || null,
    Platform: null,
    PSID: null,
    fireberryAccountLink: contact.fireberryId || null,
    SearchTermResult: null,
    ComposedName: null,
    SearchIndexName: null,
    OwnerCompanyCustomerId: null,
    Role: null,
    isNew: null,
    DontDisturb: null,
    OpenTime: contact.lastSession?.dateStart || null,
    IncomingLastMessageMediaType: lastIncoming ? mapMessageTypeToMediaType(lastIncoming.type) : null,
    IncomingLastMessageSource: lastIncoming?.status || null,
    IncomingLastMessageContactUser: null,
    OutgoingLastMessageMediaType: lastOutgoing ? mapMessageTypeToMediaType(lastOutgoing.type) : null,
    OutgoingLastMessageSource: lastOutgoing?.status || null,
    UnreadNumber: contact.lastSession?.unreadCount ?? contact.unreadCount ?? 0,
    isBot: contact.activeBot !== null && contact.activeBot !== undefined,
    SessionId: contact.lastSession?.id || null, // שמירת מזהה הסשן
    marketingConsentLevel: contact.marketingConsentLevel ?? null, // רמת הסכמה שיווקית
  };
}

/**
 * ממיפה את סוג ההודעה (type) ל-MessageMediaType
 */
function mapMessageTypeToMediaType(type: string | null | undefined): MessageMediaType | null {
  if (!type) return null;
  
  const typeMap: Record<string, MessageMediaType> = {
    'text': 'text',
    'image': 'image',
    'video': 'video',
    'audio': 'audio',
    'file': 'document',
    'location': 'location',
    'contact': 'contacts',
    'sticker': 'sticker',
    'template': 'template',
  };
  
  return typeMap[type.toLowerCase()] || null;
}

/**
 * ממיפה את הסטטוס ל-MessageStatus
 */
function mapStatusToMessageStatus(status: string | null | undefined): MessageStatus | null {
  if (!status) return null;
  
  const statusMap: Record<string, MessageStatus> = {
    'sent': 'sent',
    'delivered': 'delivered',
    'read': 'read',
    'failed': 'failed',
    'received': 'received',
  };
  
  return statusMap[status.toLowerCase()] || null;
}

